import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  CreditCard, Building2, ArrowLeft, CheckCircle2, 
  ExternalLink, Loader2, AlertCircle, Shield, Clock
} from 'lucide-react';
import Logo from '../Logo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CharityPaymentSetup({ charity, onLogout }) {
  const [searchParams] = useSearchParams();
  const [stripeStatus, setStripeStatus] = useState(null);
  const [manualStatus, setManualStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showManualForm, setShowManualForm] = useState(false);
  
  const [bankForm, setBankForm] = useState({
    legal_name: '',
    ein_tax_id: '',
    bank_name: '',
    bank_routing_number: '',
    bank_account_number: '',
    account_type: 'checking'
  });

  useEffect(() => {
    loadPaymentStatus();
    
    // Check for Stripe callback
    if (searchParams.get('success') === 'true') {
      toast.success('Stripe account connected successfully!');
      loadPaymentStatus();
    }
    if (searchParams.get('refresh') === 'true') {
      toast.info('Please complete your Stripe setup');
    }
  }, [searchParams]);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('charity_session_token')}`,
    'Content-Type': 'application/json'
  });

  const loadPaymentStatus = async () => {
    try {
      const [stripeRes, manualRes] = await Promise.all([
        fetch(`${API_URL}/api/charity-portal/payment/stripe/status`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/charity-portal/payment/manual/status`, { headers: getAuthHeaders() })
      ]);

      if (stripeRes.ok) {
        setStripeStatus(await stripeRes.json());
      }
      if (manualRes.ok) {
        setManualStatus(await manualRes.json());
      }
    } catch (error) {
      console.error('Failed to load payment status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectStripe = async () => {
    setConnecting(true);
    try {
      const response = await fetch(`${API_URL}/api/charity-portal/payment/stripe/connect`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe
        window.location.href = data.url;
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to connect Stripe');
      }
    } catch (error) {
      toast.error('Failed to connect Stripe');
    } finally {
      setConnecting(false);
    }
  };

  const handleSubmitManual = async (e) => {
    e.preventDefault();
    
    // Validate
    if (!bankForm.legal_name || !bankForm.ein_tax_id || !bankForm.bank_name || 
        !bankForm.bank_routing_number || !bankForm.bank_account_number) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (bankForm.bank_routing_number.length !== 9) {
      toast.error('Routing number must be 9 digits');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/charity-portal/payment/manual`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(bankForm)
      });

      if (response.ok) {
        toast.success('Bank details submitted! We\'ll verify within 3-5 business days.');
        setShowManualForm(false);
        loadPaymentStatus();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit bank details');
      }
    } catch (error) {
      toast.error('Failed to submit bank details');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/charity" className="text-gray-600 hover:text-gray-900">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="font-semibold text-gray-900">Payment Setup</h1>
              <p className="text-xs text-gray-500">Configure how you receive donations</p>
            </div>
          </div>
          <Logo size="small" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
          </div>
        ) : (
          <>
            {/* Stripe Connect Option */}
            <Card className={stripeStatus?.payouts_enabled ? 'border-emerald-500 border-2' : ''}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                      <CreditCard className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        Connect with Stripe
                        <Badge className="bg-emerald-500 text-xs">Recommended</Badge>
                      </CardTitle>
                      <CardDescription>
                        Instant setup, automatic payouts, no bank details needed
                      </CardDescription>
                    </div>
                  </div>
                  {stripeStatus?.payouts_enabled && (
                    <Badge className="bg-emerald-500">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Connected
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-3 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Shield className="w-4 h-4 text-emerald-600" />
                    <span>Bank-level security</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <span>Automatic tax forms</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Clock className="w-4 h-4 text-emerald-600" />
                    <span>2-day payouts</span>
                  </div>
                </div>

                {stripeStatus?.connected ? (
                  <div className="p-4 bg-emerald-50 rounded-lg">
                    <div className="flex items-center gap-2 text-emerald-700">
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="font-medium">
                        {stripeStatus.payouts_enabled 
                          ? 'Your Stripe account is fully connected and ready for payouts!'
                          : 'Stripe connected - complete setup to enable payouts'}
                      </span>
                    </div>
                    {!stripeStatus.payouts_enabled && (
                      <Button
                        className="mt-3 bg-purple-600 hover:bg-purple-700"
                        onClick={handleConnectStripe}
                        disabled={connecting}
                      >
                        {connecting ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <ExternalLink className="w-4 h-4 mr-2" />
                        )}
                        Complete Stripe Setup
                      </Button>
                    )}
                  </div>
                ) : (
                  <Button
                    className="w-full sm:w-auto bg-purple-600 hover:bg-purple-700"
                    onClick={handleConnectStripe}
                    disabled={connecting}
                  >
                    {connecting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Connect with Stripe
                      </>
                    )}
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Manual Bank Transfer Option */}
            <Card className={manualStatus?.submitted ? 'border-blue-500 border-2' : ''}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle>Manual Bank Transfer</CardTitle>
                      <CardDescription>
                        Provide bank details for ACH transfers (requires verification)
                      </CardDescription>
                    </div>
                  </div>
                  {manualStatus?.submitted && (
                    <Badge className={manualStatus.verification_status === 'verified' ? 'bg-emerald-500' : 'bg-yellow-500'}>
                      {manualStatus.verification_status === 'verified' ? (
                        <><CheckCircle2 className="w-3 h-3 mr-1" /> Verified</>
                      ) : (
                        <><Clock className="w-3 h-3 mr-1" /> Under Review</>
                      )}
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {manualStatus?.submitted ? (
                  <div className="p-4 bg-blue-50 rounded-lg space-y-2">
                    <div className="grid sm:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Legal Name:</span>
                        <p className="font-medium">{manualStatus.legal_name}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Bank:</span>
                        <p className="font-medium">{manualStatus.bank_name}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Account:</span>
                        <p className="font-medium">****{manualStatus.account_last4} ({manualStatus.account_type})</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Status:</span>
                        <p className="font-medium capitalize">{manualStatus.verification_status}</p>
                      </div>
                    </div>
                    {manualStatus.verification_status === 'under_review' && (
                      <p className="text-sm text-blue-600 mt-2">
                        <AlertCircle className="w-4 h-4 inline mr-1" />
                        We're reviewing your details. This usually takes 3-5 business days.
                      </p>
                    )}
                  </div>
                ) : showManualForm ? (
                  <form onSubmit={handleSubmitManual} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="legal_name">Legal Organization Name *</Label>
                        <Input
                          id="legal_name"
                          value={bankForm.legal_name}
                          onChange={(e) => setBankForm({ ...bankForm, legal_name: e.target.value })}
                          placeholder="Must match bank account name"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="ein">EIN / Tax ID *</Label>
                        <Input
                          id="ein"
                          value={bankForm.ein_tax_id}
                          onChange={(e) => setBankForm({ ...bankForm, ein_tax_id: e.target.value })}
                          placeholder="XX-XXXXXXX"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="bank_name">Bank Name *</Label>
                      <Input
                        id="bank_name"
                        value={bankForm.bank_name}
                        onChange={(e) => setBankForm({ ...bankForm, bank_name: e.target.value })}
                        placeholder="e.g., Chase, Bank of America"
                        required
                      />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="routing">Routing Number *</Label>
                        <Input
                          id="routing"
                          value={bankForm.bank_routing_number}
                          onChange={(e) => setBankForm({ ...bankForm, bank_routing_number: e.target.value.replace(/\D/g, '').slice(0, 9) })}
                          placeholder="9 digits"
                          maxLength={9}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="account">Account Number *</Label>
                        <Input
                          id="account"
                          value={bankForm.bank_account_number}
                          onChange={(e) => setBankForm({ ...bankForm, bank_account_number: e.target.value.replace(/\D/g, '') })}
                          placeholder="Account number"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="account_type">Account Type *</Label>
                      <Select
                        value={bankForm.account_type}
                        onValueChange={(value) => setBankForm({ ...bankForm, account_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="checking">Checking</SelectItem>
                          <SelectItem value="savings">Savings</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowManualForm(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        className="bg-blue-600 hover:bg-blue-700"
                        disabled={submitting}
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            Submitting...
                          </>
                        ) : (
                          'Submit for Verification'
                        )}
                      </Button>
                    </div>
                  </form>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full sm:w-auto"
                    onClick={() => setShowManualForm(true)}
                  >
                    <Building2 className="w-4 h-4 mr-2" />
                    Enter Bank Details
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Info Box */}
            <Card className="bg-gray-50 border-dashed">
              <CardContent className="p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Why we recommend Stripe</h3>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• <strong>Faster setup:</strong> Connect in minutes, no paperwork</li>
                  <li>• <strong>Automatic compliance:</strong> Stripe handles W-9 and 1099 forms</li>
                  <li>• <strong>Secure:</strong> You never share bank details with us</li>
                  <li>• <strong>Faster payouts:</strong> 2-day transfers vs 5-7 days for ACH</li>
                </ul>
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
